// src/Components/Services/CommonMedicalConditions.jsx
import React, { useRef, useState } from "react";
import { useNavigateAndScroll } from "../../Hooks/useNavigateAndScroll";

export default function CommonMedicalConditions() {
  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();

  // Top sticky bar (links to other services pages)
  const infoCardsData = {
    expectantParents: {
      image:
        "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/expectant_parents.webp",
      infoTitle: "Expectant Parents",
      infoText:
        "Comprehensive prenatal care, guidance, and support for expecting mothers and families preparing for their new arrival.",
      path: "/services/expectant-parents",
    },
    wellChecks: {
      image:
        "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Well+Check.webp",
      infoTitle: "Well Checks",
      infoText:
        "Regular preventive care appointments to monitor your child's growth, development, and overall health at every stage.",
      path: "/services/well-checks",
    },
    sameDaySickVisits: {
      image:
        "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/sick+same+day.webp",
      infoTitle: "Same Day Sick Visits",
      infoText:
        "Prompt medical attention for acute illnesses and urgent health concerns when your child needs immediate care.",
      path: "/services/same-day-sick-visits",
    },
    functionalMedicine: {
      image:
        "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Functional+Medicine.webp",
      infoTitle: "Functional Medicine",
      infoText:
        "Holistic approach to healthcare focusing on identifying and addressing the root causes of health issues for optimal wellness.",
      path: "/services/functional-medicine",
    },
    commonMedicalConditions: {
      image:
        "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/common+medical+conditions.webp",
      infoTitle: "Common Medical Conditions",
      infoText:
        "Information and treatment options for frequently encountered pediatric and family health conditions and concerns.",
      path: "/services/common-medical-conditions",
    },
  };

  const [expanded, setExpanded] = useState({
    adhd: false,
    allergies: false,
    asthma: false,
    autism: false,
  });

  const toggle = (key) =>
    setExpanded((s) => ({
      ...s,
      [key]: !s[key],
    }));

  return (
    <div>
      {/* Banner */}
      <div className="relative w-auto z-1 mt-[-120px]">
        <img
          className="w-full h-auto rounded-lg shadow-lg mt-2"
          src="https://cipa-website-forms.s3.us-east-2.amazonaws.com/common+med+cond.webp"
          alt="Mother and child meeting a doctor"
        />
      </div>

      {/* Sticky service nav */}
      <div
        ref={barRef}
        className="sticky top-0 flex justify-around bg-custom-blue text-white z-50"
      >
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>

      {/* Page content */}
      <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
        {/* Main page title */}
        <header className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900">
            Common Medical Conditions
          </h1>
          <div className="mt-3 h-1.5 w-auto bg-custom-regularOrange rounded"></div>
        </header>

        <div className="space-y-12 text-gray-800">
          {/* ==================== ADHD ==================== */}
          <article className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Managing ADHD: A Guide for Parents
              </h2>
              <div className="mt-2 h-1 w-auto bg-custom-blue rounded"></div>
            </div>

            {/* Always visible: What is ADHD? */}
            <section>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                What is ADHD?
              </h3>
              <p>
                Attention Deficit Hyperactivity Disorder (ADHD) is a common
                neurodevelopmental condition that primarily affects children but
                can persist into adulthood. It often leads to challenges with
                attention, focus, and self-regulation, impacting academic
                performance, social relationships, and workplace productivity.
              </p>
            </section>

            {/* Hidden content */}
            {expanded.adhd && (
              <div className="space-y-6">
                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Signs and Symptoms of ADHD
                  </h3>
                  <p className="mb-4">
                    ADHD manifests in various ways, and the following are some
                    of the common signs, as outlined by the Child Development
                    Institute:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Constant fidgeting or squirming</li>
                    <li>Difficulty remaining seated or still</li>
                    <li>Running or climbing in inappropriate situations</li>
                    <li>Excessive talking</li>
                    <li>Struggling to focus on details</li>
                    <li>Trouble listening and following directions</li>
                    <li>Chronic disorganization and misplacing items</li>
                    <li>Being easily distracted or forgetful</li>
                  </ul>
                  <p className="mt-4">
                    If your child exhibits several of these behaviors
                    consistently, it may be time to seek professional advice.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    How Pediatricians Diagnose ADHD
                  </h3>
                  <p>
                    Diagnosing ADHD involves a thorough evaluation by your
                    pediatrician, who may use screening tools and questionnaires
                    to assess your child's behavior, focus, and overall
                    functioning. Input from parents, teachers, and caregivers is
                    also critical to gaining a complete picture.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Treatment Options for ADHD
                  </h3>
                  <p className="mb-3">
                    Effective treatment for ADHD often involves a multi-faceted
                    approach, which may include:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>
                      <span className="font-semibold">Behavioral Therapy:</span>{" "}
                      Helps address and reduce inappropriate behaviors.
                    </li>
                    <li>
                      <span className="font-semibold">
                        Lifestyle Counseling:
                      </span>{" "}
                      Encourages healthy habits, such as regular exercise and a
                      balanced diet, which can support brain function and
                      emotional regulation.
                    </li>
                    <li>
                      <span className="font-semibold">Medication:</span>{" "}
                      Enhances brain activity to improve focus, concentration,
                      and self-control.
                    </li>
                  </ul>
                  <p className="mt-4">
                    We will work with you to create a tailored treatment plan
                    that meets your child's specific needs.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    The Importance of Treating ADHD
                  </h3>
                  <p className="mb-3">
                    Untreated ADHD can lead to long-term challenges that extend
                    into adulthood, such as:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Academic underachievement and poor grades</li>
                    <li>Learning difficulties</li>
                    <li>Aggressive or disruptive behaviors</li>
                    <li>Trouble forming and maintaining friendships</li>
                    <li>Difficulty in personal relationships</li>
                    <li>
                      Workplace struggles, including poor performance and
                      trouble collaborating
                    </li>
                  </ul>
                  <p className="mt-4">
                    Early diagnosis and treatment can help your child build
                    essential skills and confidence, setting them up for future
                    success.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Get Help for ADHD Today
                  </h3>
                  <p>
                    If you suspect your child may have ADHD, don't wait to seek
                    professional support. At Central Illinois Pediatric
                    Associates, we are here to guide you and your child every
                    step of the way. Contact our office at{" "}
                    <a href="tel:+13096631011" className="text-blue-600 underline">
                      (309) 663-1011
                    </a>{" "}
                    to schedule an appointment today!
                  </p>
                </section>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggle("adhd")}
              aria-expanded={expanded.adhd}
              className="inline-flex items-center text-blue-700 hover:text-blue-900 font-medium"
            >
              {expanded.adhd ? "Show Less…" : "Show More…"}
            </button>
          </article>

          {/* ==================== Allergies ==================== */}
          <article className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Managing Allergies in Children: A Guide for Parents
              </h2>
              <div className="mt-2 h-1 w-auto bg-custom-blue rounded"></div>
            </div>

            {/* Always visible: What Are Allergies? */}
            <section>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                What Are Allergies?
              </h3>
              <p>
                Allergies are a common condition that affects both children and
                adults. They may occur seasonally or persist throughout the
                year, depending on the allergens involved. At Central Illinois
                Pediatric Associates, our experienced pediatric team is here to
                help your child find relief from bothersome allergy symptoms.
              </p>
            </section>

            {/* Hidden content */}
            {expanded.allergies && (
              <div className="space-y-6">
                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Causes of Allergies
                  </h3>
                  <p className="mb-3">
                    Allergy symptoms are triggered when your child’s immune
                    system reacts to an allergen. Common allergens fall into
                    three categories:
                  </p>
                  <div className="space-y-2">
                    <p className="font-semibold">Seasonal Allergens:</p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                      <li>Plant and flower pollens</li>
                      <li>Trees and grasses</li>
                    </ul>
                    <p className="font-semibold mt-3">Year-Round Allergens:</p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                      <li>Pet fur and dander</li>
                      <li>Mold and mildew</li>
                      <li>Dust and dust mites</li>
                    </ul>
                    <p className="font-semibold mt-3">Food Allergens:</p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                      <li>Dairy</li>
                      <li>Peanuts</li>
                      <li>Shellfish</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Symptoms of Allergies in Children
                  </h3>
                  <p className="mb-3">
                    Because children have smaller nasal passages and sinuses,
                    allergy symptoms can be more pronounced. Common signs
                    include:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Itchy, watery eyes</li>
                    <li>Itchy, runny nose</li>
                    <li>Sneezing, coughing, or wheezing</li>
                    <li>Rashes that itch or burn</li>
                    <li>Breathing difficulties</li>
                    <li>Sore throat or nausea</li>
                  </ul>
                  <p className="mt-4">
                    If your child experiences these symptoms frequently, consult
                    a pediatrician for an evaluation.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    How Allergies Are Diagnosed
                  </h3>
                  <p className="mb-3">
                    Your pediatrician may use one or more of the following
                    methods to diagnose allergies:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>
                      <span className="font-semibold">Skin Testing:</span> A
                      small amount of an allergen is introduced under the skin
                      to check for a reaction.
                    </li>
                    <li>
                      <span className="font-semibold">Challenge Testing:</span>{" "}
                      For suspected food allergies, your child may ingest a
                      small amount of the allergen under medical supervision.
                    </li>
                    <li>
                      <span className="font-semibold">Blood Tests:</span> A
                      blood sample is analyzed in a laboratory to identify
                      specific allergens.
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Treatment Options for Allergies
                  </h3>
                  <p className="mb-3">
                    Several effective treatments are available to help manage
                    allergies, including:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Prescription-strength antihistamines and decongestants</li>
                    <li>Steroid nasal sprays</li>
                    <li>Allergy shots or sublingual immunotherapy (allergy drops)</li>
                    <li>Rescue inhalers for respiratory symptoms</li>
                    <li>Epi-pens for emergency allergy relief</li>
                  </ul>
                  <p className="mt-4">
                    We will recommend a personalized treatment plan based on
                    your child’s needs.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    When to Seek Emergency Care for Allergies
                  </h3>
                  <p className="mb-3">
                    A severe allergic reaction (anaphylaxis) can be
                    life-threatening. Call 911 immediately if your child:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Becomes pale or loses consciousness</li>
                    <li>Experiences severe difficulty breathing</li>
                    <li>Is unable to speak due to throat swelling</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Schedule Allergy Testing and Care Today
                  </h3>
                  <p>
                    If your child is struggling with allergies, help is
                    available. At Central Illinois Pediatric Associates, we
                    offer comprehensive allergy evaluations, including
                    respiratory allergen skin testing, and a range of treatment
                    options to provide relief. Contact our office at{" "}
                    <a href="tel:+13096631011" className="text-blue-600 underline">
                      (309) 663-1011
                    </a>{" "}
                    to schedule your appointment today!
                  </p>
                </section>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggle("allergies")}
              aria-expanded={expanded.allergies}
              className="inline-flex items-center text-blue-700 hover:text-blue-900 font-medium"
            >
              {expanded.allergies ? "Show Less…" : "Show More…"}
            </button>
          </article>

          {/* ==================== Asthma ==================== */}
          <article className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Asthma Care for Children: A Guide for Parents
              </h2>
              <div className="mt-2 h-1 w-auto bg-custom-blue rounded"></div>
            </div>

            {/* Always visible: first two paragraphs */}
            <section className="space-y-4">
              <p>
                Asthma is a chronic respiratory condition that narrows the
                airways, making breathing difficult. In children, its prevalence
                is increasing, now affecting approximately 1 in 10. Left
                unmanaged, asthma can disrupt daily activities, limit
                participation in sports, and impact school performance.
                Recognizing the warning signs—such as wheezing, persistent
                coughing, or shortness of breath—is critical, as asthma attacks
                in children can escalate quickly and cause significant distress.
              </p>
              <p>
                At Central Illinois Pediatric Associates, our dedicated
                pediatric specialists provide compassionate care tailored to
                managing childhood asthma. If your child struggles with
                nighttime coughing, wheezing, or difficulty breathing, we’re
                here to help. Our team will develop a personalized asthma action
                plan, guiding medication use and strategies to avoid triggers,
                so your child can breathe freely, play actively, and live fully.
              </p>
            </section>

            {/* Hidden content */}
            {expanded.asthma && (
              <div className="space-y-6">
                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Understanding Asthma: Causes, Symptoms, and Prevention
                  </h3>
                  <p className="mb-3">
                    Asthma in children is influenced by both genetic and
                    environmental factors. When airways become inflamed, they
                    narrow, making it harder to breathe. Common symptoms
                    include:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Wheezing</li>
                    <li>Persistent coughing (especially during play or at night)</li>
                    <li>Shortness of breath</li>
                    <li>Chest tightness</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Prevention Strategies
                  </h3>
                  <p className="mb-3">Managing asthma involves proactive prevention:</p>
                  <div className="space-y-2">
                    <p className="font-semibold">Reduce Exposure to Irritants:</p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                      <li>Avoid tobacco smoke and allergens like pollen, mold, and pet dander.</li>
                      <li>Use air purifiers and create a smoke-free home environment.</li>
                    </ul>
                    <p className="font-semibold mt-3">Develop an Asthma Action Plan:</p>
                    <ul className="list-disc list-outside ml-5 space-y-1">
                      <li>Work with our pediatric team to create a customized plan.</li>
                      <li>This plan includes daily medication schedules and specific steps to handle flare-ups.</li>
                    </ul>
                    <p className="mt-3">
                      By limiting triggers and empowering your child with knowledge, you can help them manage their
                      condition confidently.
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Treatment Options for Asthma
                  </h3>
                  <p className="mb-3">
                    Effective asthma management requires a combination of quick-relief and long-term control strategies:
                  </p>
                  <div className="space-y-2">
                    <p className="font-semibold">Quick-Relief Medications</p>
                    <p>
                      Medications like albuterol provide immediate relief during flare-ups by relaxing and opening
                      airways. These are used as needed when symptoms arise.
                    </p>
                    <p className="font-semibold mt-2">Long-Term Controller Medications</p>
                    <p>
                      Inhaled corticosteroids (ICS), such as fluticasone, help reduce airway inflammation and prevent
                      asthma attacks over time.
                    </p>
                    <p className="mt-2">
                      Our pediatricians will work closely with you to ensure the right balance between quick-relief and
                      daily controller medications, tailored to your child’s unique needs.
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Take Action for Your Child’s Health
                  </h3>
                  <p>
                    If your child has asthma, early intervention is key to preventing breathing complications and
                    enabling them to lead a happy, active life. At Central Illinois Pediatric Associates, we specialize
                    in pediatric asthma care and are committed to helping your child thrive.
                  </p>
                  <p className="mt-2">
                    To schedule an appointment or learn more about our services, call us today at{" "}
                    <a href="tel:+13096631011" className="text-blue-600 underline">
                      (309) 663-1011
                    </a>
                    . Together, we’ll ensure your child receives the expert care they deserve.
                  </p>
                </section>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggle("asthma")}
              aria-expanded={expanded.asthma}
              className="inline-flex items-center text-blue-700 hover:text-blue-900 font-medium"
            >
              {expanded.asthma ? "Show Less…" : "Show More…"}
            </button>
          </article>

          {/* ==================== Autism ==================== */}
          <article className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Autism Spectrum Disorder (ASD): A Guide for Parents
              </h2>
              <div className="mt-2 h-1 w-auto bg-custom-blue rounded"></div>
            </div>

            {/* Always visible: first two paragraphs */}
            <section className="space-y-4">
              <p>
                Autism Spectrum Disorder (ASD) is a developmental condition that affects social interaction,
                communication, and behavior. While some individuals with ASD face challenges in these areas, many also
                possess exceptional abilities in fields such as visualization, music, mathematics, and art. At Central
                Illinois Pediatric Associates, we provide compassionate care tailored to the unique needs of each
                patient, focusing on their strengths while addressing their challenges.
              </p>
              <p>
                Over the past 40 years, autism diagnoses in the United States have increased tenfold, highlighting the
                importance of understanding this condition and advancing treatment strategies.
              </p>
            </section>

            {/* Hidden content */}
            {expanded.autism && (
              <div className="space-y-6">
                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    What Causes Autism?
                  </h3>
                  <p>
                    Research indicates that autism has a strong genetic basis, though environmental factors may also
                    contribute. Studies have found structural and functional differences in the brains of children with
                    autism compared to their neurotypical peers. While the exact causes remain complex and not fully
                    understood, ongoing research continues to shed light on this multifaceted condition.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Recognizing the Individuality of Autism
                  </h3>
                  <p className="mb-3">
                    Every individual with autism experiences the condition differently. While some may have intellectual
                    disabilities, many have average or above-average intelligence, with some demonstrating extraordinary
                    talents as autistic savants.
                  </p>
                  <p className="mb-2">Communication challenges vary widely:</p>
                  <ul className="list-disc list-outside ml-5 space-y-2">
                    <li>Some individuals may struggle with verbal communication but thrive in non-verbal forms.</li>
                    <li>
                      With guidance and support, many learn effective ways to communicate and build meaningful
                      connections.
                    </li>
                  </ul>
                  <p className="mt-4">
                    By fostering understanding, respect, and support, we can help individuals with autism lead
                    fulfilling lives.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    The Role of Compassionate Care
                  </h3>
                  <p className="mb-3">
                    Autism is diagnosed more frequently in boys than girls, with boys being 4-5 times more likely to be
                    affected. Regardless of gender, individuals with autism often face stigma from peers, classmates,
                    and even family members. That’s why seeking a supportive and experienced healthcare provider, like
                    our team at Central Illinois Pediatric Associates, is crucial.
                  </p>
                  <p>
                    Our pediatric specialists are dedicated to creating a safe, accepting environment for patients and
                    their families. We focus on empowering children with autism by addressing their specific needs and
                    celebrating their unique strengths.
                  </p>
                </section>

                <section>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    Advancing Awareness and Support
                  </h3>
                  <p className="mb-3">
                    Supporting individuals with autism begins with understanding and acceptance. If you have questions
                    about autism or want to learn more about spreading awareness and fostering inclusivity, our experts
                    are here to help.
                  </p>
                  <p>
                    Contact Central Illinois Pediatric Associates at{" "}
                    <a href="tel:+13096631011" className="text-blue-600 underline">
                      (309) 663-1011
                    </a>{" "}
                    to schedule an appointment or learn more about our autism care services. Together, we can build a
                    brighter future for children with autism.
                  </p>
                </section>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggle("autism")}
              aria-expanded={expanded.autism}
              className="inline-flex items-center text-blue-700 hover:text-blue-900 font-medium"
            >
              {expanded.autism ? "Show Less…" : "Show More…"}
            </button>
          </article>
        </div>
      </div>
    </div>
  );
}
